import { isNonNullish } from '@repo/internal-tools-ts-utils/src/lib/isNonNullish.ts'
import type { AnyFn } from '@repo/internal-tools-type-utils/src/primitives.ts'

/**
 * Check if a value would be rendered when passed to JSX / React children.
 *
 * Intended for guarding `ReactNode` props (children, slots) before wrapping
 * them, e.g. `canRenderInJsx(slot) && <Wrapper>{slot}</Wrapper>`.
 *
 * React omits exactly three kinds of children: `null`, `undefined`, and
 * booleans. Everything else a `ReactNode` can hold — strings, numbers,
 * bigints, React elements, arrays, portals, and promises — is rendered, so
 * this returns `true` for them.
 *
 * Functions and symbols are also rejected: they are never renderable, so
 * guarding them is safe and defends against loosely-typed (`unknown` / `any`)
 * callers. They cannot occur in a well-typed `ReactNode`.
 */
export const canRenderInJsx = <T>(
  value: T,
): value is NonNullable<Exclude<T, boolean | symbol | AnyFn>> => {
  return (
    isNonNullish(value) &&
    typeof value !== 'boolean' &&
    typeof value !== 'function' &&
    typeof value !== 'symbol'
  )
}
